import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { flatten, pick } from 'lodash';
import { createSelector } from 'reselect';
import { replace } from 'react-router-redux';
import ColoredButton from 'components/ColoredButton';
import { Input } from 'react-toolbox';
import MessageBox from 'components/MessageBox';
import {
  resetPhasePagination,
  createSavedFilter,
  updateSavedFilter,
} from 'actionCreators';
import requestFlagOptions from 'actionCreators/flags';
import { getFlagOptions } from '../../../../../../reducers';
import ReactSelect from 'components/StyledSelect/react_select';
import TaskSelect from 'components/formElements/TaskSelect';
import UserSearch from 'components/UserSearch';
import { error } from '../../../../../../utils';
import { ThemeProvider } from 'react-css-themr';
import RTInput from 'styles/input';
import reactSelectStyles from 'components/ReactSelect/styles';
import phaseFilterStyles from 'phaseFilters.scss';
import styles from 'styles/form';
import FilterType from 'types/filter';
import { urls } from 'helpers/url';

const theme = { RTInput };
const selectStyles = `${reactSelectStyles.selectWrapper} ${phaseFilterStyles.selectWrapper}`;

const STATUS_OPTIONS = flatten(Object.keys(window.SPOT.phase_statuses).map(type =>
  [{ label: `${type} - All Statuses`, value: type, type }]
  .concat(window.SPOT.phase_statuses[type].map((status) => {
    const labelStatus = status[0] === 'provider hold' ? 'Provider Hold' : status[0];
    return { label: `${type} - ${labelStatus}`, value: type + status[1], type, status: status[1] };
  })),
));

class PhaseFilters extends Component {
  constructor(props) {
    super(props);
    this.handleTasks = this.handleSelectChange.bind(this, 'task');
  }

  componentDidMount = () => this.props.requestFlagOptions();

  updateFilters = newFilters => this.props.onFilterChange(newFilters);

  handleSelectChange = (key, values) => this.updateFilters({
    ...this.props.filters, [key]: values.map(v => v.value),
  });

  handleEstimators = values => this.updateFilters({
    ...this.props.filters,
    estimator: values.map(v => v.employable_id),
  });

  handleFlag = values => this.updateFilters({
    ...this.props.filters,
    flag: values.map(v => ({ name: v.name, suboption: v.suboption })),
  });

  handleStatus = values => this.updateFilters({
    ...this.props.filters,
    phase: values.map(v => ({ type: v.type, status: v.status })),
  });

  handleCancel = () => this.props.onStopEdit();

  handleSave = (e) => {
    e.preventDefault();
    const { filters, onStopEdit, savedFilterName } = this.props;
    this.props.createSavedFilter({ name: savedFilterName, filters })
      .then(newFilter => onStopEdit(newFilter.payload))
      .catch(() => {});
  }

  handleUpdate = (e) => {
    e.preventDefault();
    const { filters, currentFilter, onStopEdit, savedFilterName } = this.props;
    const filterParams = { filters, name: savedFilterName };
    this.props.updateSavedFilter(filterParams, currentFilter.id)
      .then(newFilter => onStopEdit(newFilter.payload))
      .catch(() => {});
  }


  render = () => (<ThemeProvider theme={theme}>
    <div className={phaseFilterStyles.content}>
      {this.props.filterErrors && this.props.filterErrors.filters &&
        <MessageBox error message={error('filters', this.props.filterErrors)} />}
      <ReactSelect
        multi
        resetValue={[]}
        name="status"
        className={selectStyles}
        options={STATUS_OPTIONS}
        value={(this.props.filters.phase || []).map(s => `${s.type}${s.status || ''}`)}
        onChange={this.handleStatus}
        label="Status"
        placeholder="All Statuses"
      />
      <TaskSelect
        value={this.props.filters.task}
        onChange={this.handleTasks}
        className={phaseFilterStyles.selectWrapper}
      />
      <ReactSelect
        multi
        resetValue={[]}
        name="flag"
        className={selectStyles}
        options={this.props.flagOptions}
        value={(this.props.filters.flag || []).map(flag => `${flag.name}${flag.suboption || ''}`)}
        onChange={this.handleFlag}
        label="Flags"
        placeholder="Any Flag"
      />
      <UserSearch
        label="Estimators"
        multi
        employableType={['Estimator']}
        initialValues={pick(this.props.filters, ['estimator'])}
        onChange={this.handleEstimators}
        className={phaseFilterStyles.selectWrapper}
      />
      <form
        onSubmit={!this.props.currentFilter ? this.handleSave : this.handleUpdate}
        className={styles.sidebarForm}
      >
        <Input
          label="Filter Name"
          className={styles.sidebarInput}
          theme={styles}
          onChange={this.props.handleFilterName}
          value={this.props.savedFilterName}
          error={error('name', this.props.filterErrors)}
        />
        <ColoredButton
          className={styles.sidebarButton}
          responsive
          color="blue"
          label={!this.props.currentFilter ? 'Save Filter' : 'Update Filter'}
          type="submit"
        />
        <ColoredButton
          className={styles.sidebarButton}
          responsive
          color="gray"
          label="Cancel"
          onClick={this.handleCancel}
        />
      </form>
      <a className={phaseFilterStyles.filtersHelpLink} href={urls.filtersHelp()} target="_blank" rel="noopener noreferrer">
        Need help setting up your filters?
      </a>
    </div>
  </ThemeProvider>);
}

const SelectOptionType = PropTypes.shape({
  label: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
});

PhaseFilters.propTypes = {
  filters: FilterType.isRequired,
  filterErrors: PropTypes.object,
  currentFilter: FilterType,
  flagOptions: PropTypes.arrayOf(SelectOptionType),
  requestFlagOptions: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  createSavedFilter: PropTypes.func.isRequired,
  updateSavedFilter: PropTypes.func.isRequired,
  onStopEdit: PropTypes.func.isRequired,
  savedFilterName: PropTypes.string.isRequired,
  handleFilterName: PropTypes.func.isRequired,
};

PhaseFilters.defaultProps = {
  filters: { workroom: [], task: [], store: [], status: [], flag: [] }, flagOptions: [],
};

const getFlagOptionsWithSuboptions = createSelector(getFlagOptions, flags => flags.map((flag) => {
  if (!flag.suboptions.length) return [{ label: flag.name, value: flag.name, name: flag.name }];
  return [{ label: `${flag.name} - All`, value: flag.name, name: flag.name }]
  .concat(flag.suboptions.map(option => ({ label: `${flag.name} - ${option}`,
    value: `${flag.name}${option}`,
    name: flag.name,
    suboption: option,
  })));
}).reduce((a, b) => a.concat(b), []));


const mapStateToProps = state => ({
  filterErrors: state.savedFilters.errors, flagOptions: getFlagOptionsWithSuboptions(state),
});

export default connect(mapStateToProps, {
  resetPhasePagination, requestFlagOptions, createSavedFilter, updateSavedFilter, replace,
})(PhaseFilters);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseList/components/PhaseFilters.jsx